import React, { useMemo, useState, useEffect } from "react";
import styles from "./ColumnStockItem.module.css";
import { getPriceLabel } from "../../utils/getPriceLabel";
import { getSwatchStyle } from "../../utils/swatchStyle";
import {
  formatRetailPrice,
  RETAIL_PRICE_LABEL,
} from "../../utils/formatRetailPrice";

import ItemImage from "Shared/ItemImage";
import ItemIconSection from "Shared/ItemIconSection";
import CartEditModal from "components/CartEditModal";
import AddedPopup from "components/AddedPopup";
import { useAddToCart } from "../../hooks/useCart";
import { lowStockMessage } from "../../utils/stockStatus";
import { trackProductAdded } from "../../utils/analytics";

import HotBadge from "assets/HotBadge.svg";
import NewBadge from "assets/NewBadge.svg";
import PreOrderIcon from "assets/PreOrder.svg";
import ThirdPartyIcon from "assets/3rdPartyIcon.svg";

function ColumnStockItem({ stockInfo, onClick }) {
  const [isCartAdded, setIsCartAdded] = useState(false);
  const [isCartModalOpen, setIsCartModalOpen] = useState(false);
  const addToCart = useAddToCart();

  useEffect(() => {
    if (isCartAdded) {
      const timer = setTimeout(() => setIsCartAdded(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isCartAdded]);

  const authorName = stockInfo.author || stockInfo.creator;
  const retailPriceText = formatRetailPrice(stockInfo.retailPrice);
  // List/columncards show the thumbnail upload, falling back to the
  // full-size image (and finally "") if only one of the two exists.
  const listImage = stockInfo?.thumbUrl || stockInfo?.imageUrl || "";

  const activate = () => onClick && onClick();
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      activate();
    }
  };

  const colorChoices = useMemo(() => {
    const options = Array.isArray(stockInfo?.detailColors)
      ? stockInfo.detailColors
      : [];

    return options
      .map((option) => {
        if (typeof option === "string") {
          return { label: option };
        }

        return {
          label: option?.label || option?.value || "",
          swatchHex: option?.swatchHex || null,
          swatchImage: option?.swatchImage || null,
        };
      })
      .filter((option) => option.label);
  }, [stockInfo?.detailColors]);

  const swatchColorForLabel = (label, index) => {
    const raw = String(label || "").trim();
    if (!raw) return "#92b7bc";

    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(raw)) {
      return raw;
    }

    const token = raw.toLowerCase();

    if (token.includes("/") && colorChoices.length === 4) {
      const figmaPalette = ["#000000", "#92B7BC", "#243746", "#FFFFFF"];
      return figmaPalette[index] || "#92b7bc";
    }

    const colorMap = {
      black: "#1e1e1e",
      white: "#f8f8f8",
      grey: "#9fa4a8",
      gray: "#9fa4a8",
      blue: "#5f86d6",
      teal: "#4fa6ad",
      navy: "#243746",
    };

    const match = Object.keys(colorMap).find((name) => token.includes(name));
    return match ? colorMap[match] : "#92b7bc";
  };

  const handleAddToCart = (updatedItem = {}) => {
    const rawProductId = stockInfo?.productId ?? stockInfo?.stockId;
    const parsedProductId = Number(rawProductId);
    const productId = Number.isNaN(parsedProductId)
      ? rawProductId
      : parsedProductId;
    const color = updatedItem?.color ?? stockInfo?.color;
    const size = updatedItem?.size ?? stockInfo?.size ?? null;
    const quantity = updatedItem?.quantity ?? stockInfo?.quantity ?? 1;

    addToCart.mutate(
      {
        productId,
        variationId: updatedItem?.variationId ?? stockInfo?.variationId ?? null,
        variantId: updatedItem?.variantId ?? stockInfo?.variantId ?? null,
        color,
        size,
        quantity,
      },
      {
        onSuccess: () => {
          setIsCartAdded(true);
          trackProductAdded(stockInfo, { quantity, color, size });
        },
      }
    );
  };

  return (
    <div className={styles.container}>
      <div
        className={styles.image}
        role="button"
        tabIndex={0}
        aria-label={`View details for ${stockInfo.title}`}
        onClick={activate}
        onKeyDown={handleKeyDown}
      >
        <ItemImage
          imageSrc={listImage}
          alt={`${stockInfo.title} Image`}
          isThumb={true}
        />
        <div className={styles.badges}>
          {stockInfo.isHot && (
            <img src={HotBadge} alt="Hot item" className={styles.flame} />
          )}

          <div className={styles.badgesRight}>
            {stockInfo.isNew && (
              <img src={NewBadge} alt="New item" className={styles.new} />
            )}
            {stockInfo.is3rdParty && (
              <img
                src={ThirdPartyIcon}
                alt="3rd party item"
                className={styles.thirdParty}
              />
            )}
          </div>
        </div>
      </div>

      <div className={styles.content}>
        <div
          className={styles.title}
          role="button"
          tabIndex={0}
          aria-label={`Open ${stockInfo.title} details`}
          onClick={activate}
          onKeyDown={handleKeyDown}
        >
          {stockInfo.title}
        </div>

        <div className={styles.costLabel}>
          {getPriceLabel(stockInfo?.itemType)}:
          <span className={styles.cost}>
            {" "}
            {stockInfo.memberPrice != null
              ? `$${stockInfo.memberPrice.toFixed(2)}`
              : "N/A"}{" "}
          </span>
        </div>

        {retailPriceText && (
          <div className={styles.costLabel}>
            {RETAIL_PRICE_LABEL}:
            <span className={styles.cost}> {retailPriceText}</span>
          </div>
        )}

        {stockInfo.subTitle && !stockInfo.isLowStock && (
          <div className={styles.subTitle}>{stockInfo.subTitle}</div>
        )}

        {authorName && (
          <div className={styles.author}>
            <span className={styles.authorLabel}>Author:</span> {authorName}
          </div>
        )}

        {colorChoices.length > 0 && (
          <div className={styles.colorOptions}>
            {colorChoices.map((option, index) => (
              <span
                key={`${option.label}-${index}`}
                className={styles.colorButton}
                aria-label={option.label}
                role="img"
                title={option.label}
                style={
                  getSwatchStyle(option) || {
                    backgroundColor: swatchColorForLabel(option.label, index),
                  }
                }
              />
            ))}
          </div>
        )}

        <div className={styles.icons}>
          <ItemIconSection
            item={{
              stockId: stockInfo.stockId,
              productId: stockInfo.productId,
              listProductId: stockInfo.listProductId,
              variationId: stockInfo.variationId,
              variantId: stockInfo.variantId,
              sortOrder: stockInfo.sortOrder,
              id: stockInfo.id,
              title: stockInfo.title,
              itemType: stockInfo.itemType,
              category: stockInfo.category,
              memberPrice: stockInfo.memberPrice,
              author: authorName,
              creator: stockInfo.creator,
              isReadable: stockInfo.isReadable,
              isLiked: stockInfo.isLiked,
              isInList: stockInfo.isInList,
              isUnavailable: stockInfo.isUnavailable,
              quantity: stockInfo.quantity ?? 1,
              color: stockInfo.color,
              size: stockInfo.size,
              image: listImage,
              imageUrl: listImage,
              isPreOrder: stockInfo.isPreOrder,
              isOutOfStock: stockInfo.isOutOfStock,
              isLowStock: stockInfo.isLowStock,
              quantityAvailable: stockInfo.quantityAvailable,
              variants: stockInfo.variants,
              detailColors: stockInfo.detailColors,
              sizeOptions: stockInfo.sizeOptions,
              variantMatrix: stockInfo.variantMatrix,
            }}
            isItemDetails={false}
            hideCart={stockInfo.isPreOrder}
            disableCart={stockInfo.isOutOfStock}
            hideOutOfStockLabel
            hideLowStockLabel
            onCartClick={handleAddToCart}
            onClick={(e) => {
              e?.stopPropagation?.();
            }}
          />

          {stockInfo.isPreOrder && (
            <button
              type="button"
              className={styles.preOrderBtn}
              aria-label="Pre-order this item"
              onClick={(e) => {
                e.stopPropagation();
                setIsCartModalOpen(true);
              }}
            >
              <img
                src={PreOrderIcon}
                alt="Pre-order item"
                className={styles.preOrderIcon}
              />
            </button>
          )}
        </div>

        {stockInfo.isOutOfStock ? (
          <div className={styles.outOfStockCorner}>Out of Stock</div>
        ) : (
          stockInfo.isLowStock && (
            <div className={styles.lowStockCorner}>
              {lowStockMessage(stockInfo.quantityAvailable)}
            </div>
          )
        )}
      </div>

      <CartEditModal
        isOpen={isCartModalOpen}
        onClose={() => setIsCartModalOpen(false)}
        onCancel={() => setIsCartModalOpen(false)}
        onSave={(updatedItem) => {
          handleAddToCart(updatedItem);
          setIsCartModalOpen(false);
        }}
        item={{
          title: stockInfo.title,
          image: listImage,
          isPreOrder: true,
          size: stockInfo.size,
          color: stockInfo?.color,
        }}
        colorOptions={colorChoices.length > 0 ? colorChoices : undefined}
      />

      <AddedPopup variants="cart" isAdded={isCartAdded} />
    </div>
  );
}

export default React.memo(ColumnStockItem);
